import { useState, useEffect } from 'react';

const Spinner = () => (
  <svg className="animate-spin h-4 w-4 inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
  </svg>
);

export default function Settings() {
  const [activeTab, setActiveTab] = useState('profile');

  // Profile state
  const [profile, setProfile] = useState({ name: 'Phil', email: 'phil@example.com' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);

  // Notifications state
  const [notifications, setNotifications] = useState({ email: true, taskCompletions: true, agentErrors: false });
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifSaved, setNotifSaved] = useState(false);

  // API Keys state
  const [apiKeys, setApiKeys] = useState([
    { id: '1', name: 'Default API Key', key: 'sk-****-****-1234', created: '2024-01-15' }
  ]);
  const [keySaving, setKeySaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchProfile = async () => {
      try {
        const response = await fetch('/api/v1/auth/me', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!cancelled && response.ok) {
          const data = await response.json();
          setProfile((p) => ({
            ...p,
            name: data.name || p.name,
            email: data.email || p.email,
          }));
        }
      } catch (err) {
        // Fall back to existing defaults
      }
    };
    fetchProfile();
    return () => { cancelled = true; };
  }, []);

  const handleProfileSave = async () => {
    setProfileSaving(true);
    await new Promise((r) => setTimeout(r, 1000));
    setProfileSaving(false);
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 2000);
  };

  const handleNotifSave = async () => {
    setNotifSaving(true);
    await new Promise((r) => setTimeout(r, 1000));
    setNotifSaving(false);
    setNotifSaved(true);
    setTimeout(() => setNotifSaved(false), 2000);
  };

  const generateApiKey = async () => {
    setKeySaving(true);
    await new Promise((r) => setTimeout(r, 500));
    const array = new Uint8Array(24);
    crypto.getRandomValues(array);
    const hex = Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
    const newKey = {
      id: Date.now().toString(),
      name: `API Key ${apiKeys.length + 1}`,
      key: `sk-${hex.slice(0, 8)}-${hex.slice(8, 16)}-${hex.slice(16, 24)}`,
      created: new Date().toISOString().split('T')[0]
    };
    setApiKeys((prev) => [...prev, newKey]);
    setKeySaving(false);
  };

  const revokeKey = (id: string) => {
    if (window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      setApiKeys((prev) => prev.filter((k) => k.id !== id));
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Settings</h1>

      <div className="flex gap-4 border-b border-gray-200 dark:border-gray-700">
        {['profile', 'notifications', 'api-keys'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-2 px-1 text-sm font-medium capitalize ${
              activeTab === tab
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {tab.replace('-', ' ')}
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        {activeTab === 'profile' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Profile Settings</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
              <input
                type="text"
                value={profile.name}
                onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              />
            </div>
            <button
              onClick={handleProfileSave}
              disabled={profileSaving}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              {profileSaving ? (
                <><Spinner /> Saving...</>
              ) : profileSaved ? (
                <>Saved! ✓</>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Notification Settings</h2>
            <div className="space-y-3">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={notifications.email}
                  onChange={(e) => setNotifications((n) => ({ ...n, email: e.target.checked }))}
                  className="w-4 h-4 text-purple-600 rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Email notifications</span>
              </label>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={notifications.taskCompletions}
                  onChange={(e) => setNotifications((n) => ({ ...n, taskCompletions: e.target.checked }))}
                  className="w-4 h-4 text-purple-600 rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Task completions</span>
              </label>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={notifications.agentErrors}
                  onChange={(e) => setNotifications((n) => ({ ...n, agentErrors: e.target.checked }))}
                  className="w-4 h-4 text-purple-600 rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Agent errors</span>
              </label>
            </div>
            <button
              onClick={handleNotifSave}
              disabled={notifSaving}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              {notifSaving ? (
                <><Spinner /> Saving...</>
              ) : notifSaved ? (
                <>Saved! ✓</>
              ) : (
                'Save Preferences'
              )}
            </button>
          </div>
        )}

        {activeTab === 'api-keys' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">API Keys</h2>
            {apiKeys.map((apiKey) => (
              <div key={apiKey.id} className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800 dark:text-gray-100">{apiKey.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{apiKey.key}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Created: {apiKey.created}</p>
                  </div>
                  <button
                    onClick={() => revokeKey(apiKey.id)}
                    className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-600 rounded hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-300"
                  >
                    Revoke
                  </button>
                </div>
              </div>
            ))}
            <button
              onClick={generateApiKey}
              disabled={keySaving}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              {keySaving ? (
                <><Spinner /> Generating...</>
              ) : (
                'Generate New Key'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
